import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import axios from "axios";
import { useMarketplaceAccounts } from "../../hooks/marketplaceAccounts";
import { useAllFx } from "../../hooks/fx";
import { getAccountPriceCheck } from "./priceCheck";

const EMPTY = [];

/**
 * Price checks for the listing's OTHER Chrono24 accounts (CH24 Europe, Dubai…)
 * — the CH24 UK check converted into each account's currency (see
 * getAccountPriceCheck). Returns { checks: [{ account, key, isListed, check }], legacy }
 * where legacy is the CH24 UK account row (its label names the main Chrono24 tab).
 *
 * @param {object} listing
 * @param {object} [opts]
 * @param {object[]} [opts.accounts]  enabled Chrono24 accounts when the caller already has them
 * @param {object}   [opts.livePrices] { [accountId]: major price } — the listing form's unsaved
 *   values. With it, the price comes from the form and every account counts as listed (the rep
 *   is looking at that tab). Each account's junction row is always read (same query key as the
 *   listing card's toggle rows, so they share the cache): it carries the account's own market.
 * @param {boolean}  [opts.enabled=true]
 */
export default function useAccountChecks(listing, { accounts: given, livePrices, enabled = true } = {}) {
  const storeIdM = listing?.store_id_m;
  // useMarketplaceAccounts hands back the cached array itself (stable between
  // renders); useChrono24Accounts would re-sort it into a new one every render.
  const { accounts: fetched } = useMarketplaceAccounts(storeIdM, { enabled: enabled && !given && !!listing?.id });
  const all = useMemo(
    () => (given || fetched || EMPTY).filter((a) => a.marketplace === "chrono24" && a.is_enabled !== false),
    [given, fetched]
  );
  const extra = useMemo(() => all.filter((a) => a.slug !== null), [all]);
  const legacy = all.find((a) => a.slug === null) || null;
  const { rates } = useAllFx(enabled && extra.length ? "GBP" : null);

  const states = useQueries({
    queries: extra.map((a) => ({
      queryKey: ["listing-marketplace-state", listing?.id, a.id],
      queryFn: async () => (await axios.get(`/api/listings/${listing.id}/marketplaces/${a.id}`)).data,
      // Fetched in the form too: the row carries the account's own market
      // (chrono_markets) even when the price comes from the unsaved form.
      enabled: enabled && !!listing?.id,
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
    })),
  });
  const stateKey = states.map((s) => s.dataUpdatedAt).join(",");

  const checks = useMemo(() => {
    if (!enabled || !listing) return EMPTY;
    return extra.map((account, i) => {
      const row = states[i]?.data;
      const live = livePrices?.[account.id];
      const price = livePrices
        ? (live === "" || live === null || live === undefined ? null : Number(live))
        // Junction price is minor units; 0 is Chrono24's "Price on request".
        : (row?.price > 1 ? Number(row.price) / 100 : null);
      return {
        account,
        key: `chrono-${account.id}`,
        isListed: livePrices ? true : !!row?.is_listed,
        check: getAccountPriceCheck(listing, account, {
          price,
          rate: rates?.[String(account.currency || "").toUpperCase()],
          market: row,
        }),
      };
    });
  }, [enabled, listing, extra, livePrices, rates, stateKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { checks, legacy };
}
